import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkTokenExpiry, login, logout, updateToken } from '../../../app/auth/authSlice';
import { isTokenExpired, shouldRefreshToken, getTimeUntilExpiry } from '../../../utils/tokenUtils';

const TokenExpiryChecker = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, keepMeLogin } = useSelector((state) => state.auth);
  const intervalRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const hasRedirectedRef = useRef(false);

  // Don't run token checks when on login page
  const isOnLoginPage = location.pathname.includes('/user/login');

  // Reset redirect flag when location changes
  useEffect(() => {
    if (!isOnLoginPage) {
      hasRedirectedRef.current = false;
    }
  }, [location.pathname, isOnLoginPage]);

  // Function to safely redirect to login
  const safeRedirectToLogin = () => {
    if (!hasRedirectedRef.current && !isOnLoginPage) {
      hasRedirectedRef.current = true;
      dispatch(logout());
      navigate('/user/login', { replace: true });
    }
  };

    // Function to proactively refresh token
  const refreshTokenProactively = async () => {
    if (!keepMeLogin || !user?.refreshToken) return false;

    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) return false;
      
      // Get the API URL from environment
      const API_URL = process.env.REACT_APP_Environment === "development"
        ? process.env.REACT_APP_API_URL
        : process.env.REACT_APP_API_URL_Production;

      const response = await fetch(`${API_URL}/api/Auth/RefreshToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status && data.data.token) {
          // Update stored tokens
          const updatedUserData = { ...user };
          updatedUserData.token = data.data.token;
          updatedUserData.keepMeLogin = keepMeLogin; // Preserve keepMeLogin
          if (data.data.refreshToken) {
            updatedUserData.refreshToken = data.data.refreshToken;
            localStorage.setItem('refreshToken', data.data.refreshToken);
          }
          localStorage.setItem('user', JSON.stringify(updatedUserData));
          
          // Update Redux store
          dispatch(updateToken({
            token: data.data.token,
            refreshToken: data.data.refreshToken || refreshToken,
            keepMeLogin: keepMeLogin
          }));
          
          return true; // Success
        }
      }
      return false; // Failed
    } catch (error) {
      console.warn('Proactive token refresh failed:', error);
      return false; // Failed
    }
  };

  // Check token expiry on component mount
  useEffect(() => {
    // Skip token checks if on login page or if user is not authenticated
    if (isOnLoginPage || !isAuthenticated || !user) return;

    if (user?.token) {
      // Check immediately
      if (isTokenExpired(user.token)) {
        // If keepMeLogin is enabled, don't logout immediately - let axios interceptor handle refresh
        if (!keepMeLogin) {
          safeRedirectToLogin();
          return;
        }
      }

      // Set up periodic checks (every 5 minutes)
      intervalRef.current = setInterval(() => {
        dispatch(checkTokenExpiry());

        // If token is expired after check and keepMeLogin is disabled, logout
        if (isTokenExpired(user.token) && !keepMeLogin) {
          safeRedirectToLogin();
        }
      }, 5 * 60 * 1000); // 5 minutes

      // Set up proactive token refresh when keepMeLogin is enabled
      if (keepMeLogin) {
        const timeUntilExpiry = getTimeUntilExpiry(user.token);
        if (timeUntilExpiry > 0) {
          // Refresh token 5 minutes before expiry, but not immediately if token is already close to expiry
          const refreshThreshold = 300; // 5 minutes
          const minDelay = 30000; // Minimum 30 seconds delay to avoid immediate refresh
          
          let refreshDelay;
          if (timeUntilExpiry <= refreshThreshold) {
            // Token is already close to expiry, refresh in 30 seconds
            refreshDelay = minDelay;
          } else {
            // Token has time left, schedule refresh for 5 minutes before expiry
            refreshDelay = Math.max((timeUntilExpiry - refreshThreshold) * 1000, minDelay);
          }
          
          refreshIntervalRef.current = setTimeout(() => {
            refreshTokenProactively();
            
            // Set up recurring refresh every hour if keepMeLogin is still enabled
            const setupRecurringRefresh = () => {
              if (keepMeLogin) {
                refreshIntervalRef.current = setInterval(() => {
                  refreshTokenProactively();
                }, 60 * 60 * 1000); // Every hour
              }
            };
            
            // Wait a bit before setting up recurring refresh to avoid conflicts
            setTimeout(setupRecurringRefresh, 5000);
          }, refreshDelay);
        }
      }
    }

    // Cleanup intervals on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (refreshIntervalRef.current) {
        clearTimeout(refreshIntervalRef.current);
      }
    };
  }, [user, dispatch, navigate, isOnLoginPage, keepMeLogin, isAuthenticated]);

  // Check token expiry when user changes
  useEffect(() => {
    // Skip token checks if on login page or if user is not authenticated
    if (isOnLoginPage || !isAuthenticated || !user) return;

    if (!isAuthenticated && user) {
      dispatch(checkTokenExpiry());
    }
  }, [user, isAuthenticated, dispatch, isOnLoginPage]);

  // Listen for storage changes (e.g., from other tabs)
  useEffect(() => {
    // Skip storage checks if on login page or if user is not authenticated
    if (isOnLoginPage || !isAuthenticated || !user) return;

    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        if (!e.newValue) {
          // User was logged out in another tab
          safeRedirectToLogin();
        } else {
          // User was logged in in another tab
          try {
            const newUser = JSON.parse(e.newValue);
            if (newUser?.token && !isTokenExpired(newUser.token)) {
              dispatch(login(newUser));
            }
          } catch (error) {
            // Error parsing user data from storage silently
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [dispatch, navigate, isOnLoginPage, safeRedirectToLogin]);

  // Listen for page visibility changes to check token when tab becomes active
  useEffect(() => {
    // Skip visibility checks if on login page or if user is not authenticated
    if (isOnLoginPage || !isAuthenticated || !user) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && user?.token) {
        dispatch(checkTokenExpiry());
        // If token is expired and keepMeLogin is disabled, logout
        if (isTokenExpired(user.token) && !keepMeLogin) {
          safeRedirectToLogin();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, dispatch, navigate, isOnLoginPage, keepMeLogin, safeRedirectToLogin]);

  // Listen for global events from axios interceptors
  useEffect(() => {
    const handleTokenRefresh = (event) => {
      const { token, refreshToken, keepMeLogin } = event.detail;
      dispatch(updateToken({ token, refreshToken, keepMeLogin }));
    };

    const handleForceLogout = () => {
      safeRedirectToLogin();
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);
    window.addEventListener('forceLogout', handleForceLogout);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
      window.removeEventListener('forceLogout', handleForceLogout);
    };
  }, [dispatch, navigate, safeRedirectToLogin]);

  // If not authenticated or on login page, don't render anything
  if (isOnLoginPage || !isAuthenticated || !user) {
    return null;
  }

  // This component doesn't render anything
  return null;
};

export default TokenExpiryChecker;

